import { PassengerType, ProductType } from "@b2bportal/core-types";
import { getPassengerType } from "@b2bportal/core-utilities";
import {
  ContactSelectors,
  ParentState,
  TravelerSelectors,
  useCheckoutSend,
  useCheckoutStateSelector,
  Event,
} from "@checkout/index";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useParentState, usePackageTravelerCountValidation } from "../../hooks";
import type { StepInfo, StepToStateMap } from "./types";
import { TermsAndConditionsSelectors } from "@/modules/checkout";
import { RewardsPaymentSelectors } from "@/modules/checkout/states/Rewards";
import { useBalanceWithPoints } from "@/overrides/apac/apac-checkout";

export const defaultStepToStateMap = {
  [ParentState.contactAndTravelerInformation]: 1,
  [ParentState.cardPayment]: 2,
  [ParentState.review]: 3,
};

type MobileCheckoutPageConfig = {
  stepToStateMap?: StepToStateMap;
  customHideDockStates?: ParentState[];
  productType?: ProductType;
};

export const useMobileCheckoutPage = ({
  stepToStateMap = defaultStepToStateMap,
  customHideDockStates = [],
  productType,
}: MobileCheckoutPageConfig = {}) => {
  const { t } = useI18nContext();

  const {
    parentStateContext: { currentState },
  } = useParentState();

  const send = useCheckoutSend();

  const isTravellerSelected = useCheckoutStateSelector(
    TravelerSelectors.getIsATravelerSelected
  );

  const isContactInfoComplete = useCheckoutStateSelector(
    ContactSelectors.getContactInformationValidated
  );

  const requiresTravelerRevalidation = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRequiresTravelerRevalidation
  );

  const selectedTravelers = useCheckoutStateSelector(
    TravelerSelectors.getAllSelectedTravelers
  );

  const {
    hasCorrectTravelerCount,
    showTravelerCountError,
    travelerCountErrorMessage,
  } = usePackageTravelerCountValidation(productType);

  const hasAdultPrimaryTraveler =
    productType !== ProductType.LODGING ||
    selectedTravelers.some(
      (traveler) => getPassengerType(traveler) === PassengerType.Adult
    );

  const showAdultTravelerError =
    isTravellerSelected && !hasAdultPrimaryTraveler;

  const canContinuePassengerInformation =
    isTravellerSelected &&
    isContactInfoComplete &&
    !requiresTravelerRevalidation &&
    hasCorrectTravelerCount &&
    hasAdultPrimaryTraveler;

  const { isFulfillDisabled } = useBalanceWithPoints();

  const isTermsAndConditionsAccepted = useCheckoutStateSelector(
    TermsAndConditionsSelectors.getTermsAndConditionsAccepted
  );

  const stepMap: Record<number, StepInfo> = {
    1: {
      buttonLabel: t("checkout.travelerInformation.continue"),
      disabled: !canContinuePassengerInformation,
      onClick: () => send(Event.NEXT),
    },
    2: {
      buttonLabel: t("continue"),
      disabled: isFulfillDisabled,
      onClick: () => send(Event.NEXT),
    },
    3: {
      buttonLabel: t("confirmAndBook"),
      disabled: !isTermsAndConditionsAccepted || isFulfillDisabled,
      onClick: () => send(Event.NEXT),
    },
  };

  const step: number = stepToStateMap[currentState] ?? 1;

  const baseHideDockStates = [
    ParentState.cartFulfill,
    ParentState.cartQuote,
    ParentState.cartUpdate,
  ];

  const allHideDockStates = [...baseHideDockStates, ...customHideDockStates];
  const hideDockedCta = allHideDockStates.some(
    (state) => ParentState[currentState] === state
  );

  const hidePriceBreakdown = ParentState.review === currentState;

  const stepInfo = stepMap[step] ?? {
    buttonLabel: t("continue"),
    disabled: false,
    onClick: () => send(Event.NEXT),
  };

  return {
    ...stepInfo,
    step,
    hideDockedCta,
    stepMap,
    hidePriceBreakdown,
    showTravelerCountError,
    showAdultTravelerError,
    travelerCountErrorMessage,
  };
};
